// Header.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
} from '@mui/material';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Récupération des informations utilisateur depuis le localStorage
  const userEmail = localStorage.getItem('email') || '';
  const userName = userEmail.split('@')[0]; // Extraction du nom à partir de l'email

  const navItems = [
    { label: 'Accueil', path: '/' },
    { label: 'À propos', path: '/' },
    { label: 'Contact', path: '/' },
  ];

  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);
  
  // Gestion de l'ouverture/fermeture du menu déroulant
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Gestion de la déconnexion
  const handleLogout = () => {
    // Suppression des données utilisateur du localStorage
    localStorage.removeItem('email');
        localStorage.removeItem('role');
    localStorage.removeItem('token');

    // Redirection vers la page de connexion (à adapter selon votre implémentation)
    window.location.href = '/login';
    handleMenuClose();
  };

  const drawer = (
    <Box sx={{ width: 280, pt: 4, px: 2 }} onClick={toggleDrawer(false)}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
         <img
          src="/WAMA.png"
          alt="WAMA Logo"
          style={{ height: '40px', objectFit: 'contain' }}
        />
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              component={Link} 
              to={item.path}
              sx={{
                borderRadius: 3,
                '&:hover': { bgcolor: 'rgba(196, 176, 140, 0.1)', color: '#C4B08C' }
              }}
            >
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
         <Divider sx={{ my: 2 }} />
         <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 3, color: '#d32f2f' }}>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          color: '#000',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', px: { xs: 2, md: 4 } }}>
          {/* Logo à gauche */}
<Box
  component={Link}
  to="/"
  sx={{
    display: 'flex', // Pour s'assurer que le contenu (l'image) est bien aligné
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <img
    src="/WAMA.png"
    alt="WAMA Logo"
    style={{
      height: '32px',
      width: 'auto',
      objectFit: 'contain',
      filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))'
    }}
  />
</Box>


          {/* Menu centré */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, justifyContent: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: '#333',
                    fontWeight: 600,
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: 'rgba(196, 176, 140, 0.1)', 
                      color: '#C4B08C',
                      transform: 'translateY(-1px)'
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Account / Menu Mobile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <>
                <IconButton 
                  onClick={handleMenuOpen}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.1)',
                    p: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#C4B08C', bgcolor: 'rgba(196, 176, 140, 0.05)' }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: '#C4B08C',
                      fontSize: '0.9rem',
                      fontWeight: 700 
                    }}
                  >
                   {userName ? userName.charAt(0).toUpperCase() : <AccountCircle />}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1.5,
                      borderRadius: 2,
                      minWidth: 200,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Box sx={{ p: 2, pb: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Avatar sx={{ bgcolor: '#C4B08C', width: 32, height: 32 }}>
                        {userName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {userEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleMenuClose} component={Link} to="/profile">
                    Profile
                  </MenuItem>
                  <MenuItem onClick={handleMenuClose} component={Link} to="/settings">
                    Settings
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}

            {isMobile && (
              <IconButton edge="end" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
                <MenuIcon sx={{ color: '#111' }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer pour mobile */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
